import { db } from './config';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';

// ===== User Management =====
export const createUser = async (userId, userData) => {
  try {
    await setDoc(doc(db, 'users', userId), {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error creating user:', error);
    return { success: false, error };
  }
};

export const getUserData = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data() };
    } else {
      return { success: false, error: 'User not found' };
    }
  } catch (error) {
    console.error('Error getting user data:', error);
    return { success: false, error };
  }
};

// ===== Screen Time Tracking =====
export const addScreenTimeEntry = async (userId, data) => {
  try {
    const docRef = await addDoc(collection(db, 'screenTime'), {
      userId,
      date: Timestamp.fromDate(new Date(data.date)),
      totalTime: data.totalTime,
      hourlyBreakdown: data.hourlyBreakdown || [],
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding screen time:', error);
    return { success: false, error };
  }
};

export const getScreenTimeData = async (userId, dateFrom, dateTo) => {
  try {
    const screenTimeRef = collection(db, 'screenTime');
    const q = query(
      screenTimeRef,
      where('userId', '==', userId),
      where('date', '>=', Timestamp.fromDate(new Date(dateFrom))),
      where('date', '<=', Timestamp.fromDate(new Date(dateTo))),
      orderBy('date', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const screenTimeData = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      screenTimeData.push({
        id: doc.id,
        date: data.date.toDate(),
        totalTime: data.totalTime,
        hourlyBreakdown: data.hourlyBreakdown || []
      });
    });

    return { success: true, data: screenTimeData };
  } catch (error) {
    console.error('Error getting screen time data:', error);
    return { success: false, error };
  }
};

// ===== App Usage Tracking =====
export const addAppUsageEntry = async (userId, data) => {
  try {
    const docRef = await addDoc(collection(db, 'appUsage'), {
      userId,
      date: Timestamp.fromDate(new Date(data.date)),
      app: data.app,
      timeSpent: data.timeSpent,
      category: data.category,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding app usage:', error);
    return { success: false, error };
  }
};

export const getAppUsageData = async (userId, dateFrom, dateTo) => {
  try {
    const appUsageRef = collection(db, 'appUsage');
    const q = query(
      appUsageRef,
      where('userId', '==', userId),
      where('date', '>=', Timestamp.fromDate(new Date(dateFrom))),
      where('date', '<=', Timestamp.fromDate(new Date(dateTo))),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const appUsageData = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      appUsageData.push({
        id: doc.id,
        date: data.date.toDate(),
        app: data.app,
        timeSpent: data.timeSpent,
        category: data.category
      });
    });

    return { success: true, data: appUsageData };
  } catch (error) {
    console.error('Error getting app usage data:', error);
    return { success: false, error };
  }
};

// ===== Goals Management =====
export const createGoal = async (userId, goalData) => {
  try {
    const docRef = await addDoc(collection(db, 'goals'), {
      userId,
      name: goalData.name,
      target: goalData.target,
      unit: goalData.unit,
      icon: goalData.icon,
      current: 0,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating goal:', error);
    return { success: false, error };
  }
};

export const updateGoalProgress = async (goalId, progress) => {
  try {
    await updateDoc(doc(db, 'goals', goalId), {
      current: progress,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating goal progress:', error);
    return { success: false, error };
  }
};

export const getUserGoals = async (userId) => {
  try {
    const goalsRef = collection(db, 'goals');
    const q = query(
      goalsRef,
      where('userId', '==', userId),
      where('isActive', '==', true)
    );

    const querySnapshot = await getDocs(q);
    const goals = [];
    
    querySnapshot.forEach((doc) => {
      goals.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, data: goals };
  } catch (error) {
    console.error('Error getting user goals:', error);
    return { success: false, error };
  }
};

// ===== Focus Sessions =====
export const startFocusSession = async (userId, sessionData) => {
  try {
    const docRef = await addDoc(collection(db, 'focusSessions'), {
      userId,
      startTime: serverTimestamp(),
      plannedDuration: sessionData.plannedDuration,
      focusType: sessionData.focusType,
      isActive: true
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error starting focus session:', error);
    return { success: false, error };
  }
};

export const endFocusSession = async (sessionId, sessionData) => {
  try {
    await updateDoc(doc(db, 'focusSessions', sessionId), {
      endTime: serverTimestamp(),
      actualDuration: sessionData.actualDuration,
      isActive: false,
      wasSuccessful: sessionData.wasSuccessful,
      notes: sessionData.notes || ''
    });
    return { success: true };
  } catch (error) {
    console.error('Error ending focus session:', error);
    return { success: false, error };
  }
};

export const getFocusSessionHistory = async (userId, limit = 10) => {
  try {
    const focusSessionsRef = collection(db, 'focusSessions');
    const q = query(
      focusSessionsRef,
      where('userId', '==', userId),
      where('isActive', '==', false),
      orderBy('startTime', 'desc'),
      limit(limit)
    );

    const querySnapshot = await getDocs(q);
    const sessions = [];
    
    querySnapshot.forEach((doc) => {
      sessions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, data: sessions };
  } catch (error) {
    console.error('Error getting focus sessions:', error);
    return { success: false, error };
  }
};

// ===== User Settings =====
export const updateUserSettings = async (userId, settings) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      settings: settings,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user settings:', error);
    return { success: false, error };
  }
};

// ===== AI Insights =====
export const saveAIInsight = async (userId, insightData) => {
  try {
    const docRef = await addDoc(collection(db, 'insights'), {
      userId,
      type: insightData.type,
      title: insightData.title,
      description: insightData.description,
      category: insightData.category,
      priority: insightData.priority,
      isRead: false,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error saving AI insight:', error);
    return { success: false, error };
  }
};

export const getUserInsights = async (userId, limit = 20) => {
  try {
    const insightsRef = collection(db, 'insights');
    const q = query(
      insightsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limit)
    );

    const querySnapshot = await getDocs(q);
    const insights = [];
    
    querySnapshot.forEach((doc) => {
      insights.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate()
      });
    });

    return { success: true, data: insights };
  } catch (error) {
    console.error('Error getting user insights:', error);
    return { success: false, error };
  }
};

export const markInsightAsRead = async (insightId) => {
  try {
    await updateDoc(doc(db, 'insights', insightId), {
      isRead: true
    });
    return { success: true };
  } catch (error) {
    console.error('Error marking insight as read:', error);
    return { success: false, error };
  }
}; 